import { Suspense, useEffect, useRef, useState } from "react";

// Mounts children only when the placeholder scrolls within `margin` of the viewport (or after idle) — keeps below-the-fold JS/data off the critical path
export const LazyMount = ({ children, minHeight = 240, margin = "600px", idleMs = 6000 }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (on) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setOn(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setOn(true); io.disconnect(); } }, { rootMargin: margin });
    io.observe(el);
    const t = setTimeout(() => setOn(true), idleMs);
    return () => { io.disconnect(); clearTimeout(t); };
  }, [on, margin, idleMs]);
  return on ? <Suspense fallback={<div style={{ minHeight }} />}>{children}</Suspense> : <div ref={ref} style={{ minHeight }} aria-hidden="true" />;
};

// Renders children after the browser is idle (first paint + `delay` ms) — for chat widgets and other non-critical overlays
export const AfterIdle = ({ children, delay = 2500 }) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let id;
    const go = () => setOn(true);
    if ("requestIdleCallback" in window) id = window.requestIdleCallback(go, { timeout: delay + 2000 });
    else id = setTimeout(go, delay);
    return () => ("cancelIdleCallback" in window ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, [delay]);
  return on ? <Suspense fallback={null}>{children}</Suspense> : null;
};
